import { useState } from 'react';

import {
  Button,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';

import { PrideRow } from 'app/utils/table/campaigns/prides';

interface ApplyToPridesModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The pride whose people are being copied, which is never a target of the copy. */
  source: PrideRow;
  targets: PrideRow[];
  names: string[];
  isSaving: boolean;
  onApply: (prideIds: string[]) => void;
}

/** Copying one pride's people onto others, for the common case of a few people deciding many prides. */
function ApplyToPridesModal({
  isOpen,
  onClose,
  source,
  targets,
  names,
  isSaving,
  onApply,
}: ApplyToPridesModalProps) {
  const [selected, setSelected] = useState<string[]>([]);

  const close = () => {
    setSelected([]);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={close} size="lg">
      <ModalOverlay />
      <ModalContent data-testid="apply-to-prides-modal">
        <ModalHeader>Apply these people to other prides</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex flexDir="column" gap="16px">
            <Text textStyle="smallRegular" color="brand.black">
              {names.join(', ')} decide {source.prideName}. Pick the prides they should decide as
              well — anyone already appointed there is left as they are.
            </Text>
            <Flex flexDir="column" gap="8px" maxH="280px" overflowY="auto">
              {targets.map((pride) => (
                <Checkbox
                  key={pride.id}
                  isChecked={selected.includes(pride.id)}
                  onChange={() =>
                    setSelected((ids) =>
                      ids.includes(pride.id)
                        ? ids.filter((id) => id !== pride.id)
                        : [...ids, pride.id]
                    )
                  }
                  data-testid={`apply-to-${pride.id}`}
                >
                  {pride.prideName}
                </Checkbox>
              ))}
            </Flex>
          </Flex>
        </ModalBody>
        <ModalFooter gap="12px">
          <Button variant="outline" onClick={close} data-testid="cancel-apply-to-prides">
            Cancel
          </Button>
          <Button
            onClick={() => onApply(selected)}
            isDisabled={selected.length === 0 || isSaving}
            isLoading={isSaving}
            data-testid="confirm-apply-to-prides"
          >
            Apply to {selected.length} {selected.length === 1 ? 'pride' : 'prides'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default ApplyToPridesModal;
